import React, { useMemo } from 'react'
import { FutureAvailability } from '../../types/menu'
import { useRestaurant } from '@local/do-secundo-restaurant-provider'
import {
  getIntervalsDescription,
  renderDatesDescription
} from '../../utils/availability-util'
import { toShortDateString } from '../../utils/time-utils'
import { useFulfillment } from '../FulfillmentProvider/FulfillmentProvider'
import { DiningOptionBehavior } from '../../types/cart'
import cx from 'classnames'

type DiningBehaviorDisplay = 'TAKE_OUT' | 'DELIVERY' | 'BOTH'

const getDiningBehavior = (
  futureAvailability: FutureAvailability[]
): DiningBehaviorDisplay => {
  const behaviors = Array.from(
    new Set(futureAvailability.flatMap((f) => f.diningBehaviors))
  )

  if (behaviors.length > 1) {
    return 'BOTH'
  }
  if (behaviors[0] === 'DELIVERY') {
    return 'DELIVERY'
  }
  return 'TAKE_OUT'
}

const getDiningBehaviorDisplay = (
  behavior: DiningOptionBehavior
): DiningBehaviorDisplay => {
  if (behavior === 'DELIVERY') {
    return 'DELIVERY'
  }
  return 'TAKE_OUT'
}

const getDiningBehaviorDescription = (key: DiningBehaviorDisplay) => {
  switch (key) {
    case 'BOTH':
      return 'pickup & delivery'
    case 'DELIVERY':
      return 'delivery'
    default:
      return 'pickup'
  }
}

const isAvailableDaily = (availabilities: FutureAvailability[]): boolean => {
  return availabilities.some(
    (availability) =>
      !availability.start &&
      !availability.end &&
      !availability.daysOfWeek.length
  )
}

interface Props {
  futureAvailability: FutureAvailability[]
  containerClassName?: string
}

const getGroupedFutureAvailability = (
  futureAvailability: FutureAvailability[]
) => {
  const grouped = futureAvailability.reduce((acc, availability) => {
    const key = JSON.stringify({
      daysOfWeek: availability.daysOfWeek,
      displayMaxDaysAhead: availability.displayMaxDaysAhead,
      start: availability.start,
      end: availability.end
    })

    if (!acc.has(key)) {
      acc.set(key, [])
    }

    acc.get(key)!.push(availability)
    return acc
  }, new Map<string, FutureAvailability[]>())

  return Array.from(grouped.values())
}

export const MenuFutureAvailability = ({
  futureAvailability,
  containerClassName
}: Props) => {
  const { restaurantInfo } = useRestaurant()
  const tz = restaurantInfo?.timeZoneId ?? undefined

  const { diningOptionBehavior, fulfillmentDateTime } = useFulfillment()

  const dayOfAvailabilities = futureAvailability.filter(
    (availability) =>
      availability.intervals && availability.intervals.length > 0
  )

  const sortedFutureAvailability = useMemo(() => {
    return getGroupedFutureAvailability(futureAvailability).reduce(
      (acc, availability) => {
        const diningBehavior = getDiningBehavior(availability)
        if (!acc[diningBehavior]) {
          acc[diningBehavior] = [availability[0]]
        } else {
          acc[diningBehavior].push(availability[0])
        }
        return acc
      },
      {} as Record<DiningBehaviorDisplay, FutureAvailability[]>
    )
  }, [futureAvailability])

  return (
    <div className={cx(containerClassName, 'text-coo-primary-text')}>
      {(Object.keys(sortedFutureAvailability) as DiningBehaviorDisplay[]).map(
        (behavior) => {
          const availabilities = sortedFutureAvailability[behavior]

          if (isAvailableDaily(availabilities)) {
            return (
              <div key={`availability-${behavior}`} className={'italic'}>
                {'Available daily for '}
                {getDiningBehaviorDescription(behavior)}{' '}
                <DisplayMaxLeadTime availabilities={availabilities} />
              </div>
            )
          } else {
            const dateRangeStr = renderDatesDescription(availabilities, tz)
            return (
              <div key={`availability-${behavior}`} className={'italic'}>
                {'Available for '}
                {getDiningBehaviorDescription(behavior)}
                {' orders on '}
                {dateRangeStr}{' '}
                <DisplayMaxLeadTime availabilities={availabilities} />
              </div>
            )
          }
        }
      )}
      {diningOptionBehavior &&
        dayOfAvailabilities.map((dayOfAvailability) => {
          const dateStr = toShortDateString(fulfillmentDateTime, tz)
          const behavior = getDiningBehaviorDisplay(diningOptionBehavior)
          return (
            <div
              key={`availability-${dateStr}-${behavior}}`}
              className={'font-semibold'}
            >
              {dayOfAvailability.intervals!! && (
                <div>
                  Available for {getDiningBehaviorDescription(behavior)} on{' '}
                  {dateStr}{' '}
                  {getIntervalsDescription(dayOfAvailability.intervals)}
                </div>
              )}
            </div>
          )
        })}
    </div>
  )
}

const DisplayMaxLeadTime = ({
  availabilities
}: {
  availabilities: FutureAvailability[]
}) => {
  if (!availabilities?.[0]?.displayMaxDaysAhead) {
    return null
  }

  return (
    <>
      (ordering opens {availabilities[0].displayMaxDaysAhead} days in advance)
    </>
  )
}
